import {
  Unsubscribe,
  connectDatabaseEmulator,
  equalTo,
  get,
  getDatabase,
  onValue,
  orderByChild,
  push,
  query,
  ref,
  remove,
  set,
  update,
} from 'firebase/database';
import { firebaseApp } from '../firebase/firebase.client';

const firebaseDatabase = getDatabase(firebaseApp);

if (process.env.REACT_APP_USE_EMULATOR === 'true') {
  if (!window.__EMULATOR_FIRESTORE_INITIALIZED__) {
    connectDatabaseEmulator(firebaseDatabase, location.hostname, 9000);
    window.__EMULATOR_FIRESTORE_INITIALIZED__ = true;
  }
}

type FilterOptions = {
  filterBy: string;
  filterByValue?: string;
};

class FirebaseDatabaseClient {
  static async getDocumentsByPath<T>(...paths: string[]): Promise<T[]> {
    const documentsRef = ref(firebaseDatabase, paths.join('/'));
    const result: T[] = await get(documentsRef)
      .then(snapshot => {
        if (!snapshot.exists()) return [];
        return snapshot.val();
      })
      .catch(() => []);
    return result;
  }

  static async getAvailableDocuments(...paths: string[]): Promise<string[]> {
    const documents = await FirebaseDatabaseClient.getDocumentsByPath(...paths);
    return Object.keys(documents);
  }

  static watchDocuments<T>(setData: (data: T[]) => void, paths: string[], options?: FilterOptions): Unsubscribe {
    const { filterBy = false, filterByValue = true } = options ?? {};

    const documentsRef = ref(firebaseDatabase, paths.join('/'));

    if (filterBy === false) {
      return onValue(documentsRef, snapshot => {
        const dataSnapshot = snapshot.val();
        setData(dataSnapshot);
      });
    }

    const filteredQuery = query(documentsRef, orderByChild(filterBy), equalTo(filterByValue));

    return onValue(filteredQuery, snapshot => {
      const dataSnapshot = snapshot.val();
      setData(dataSnapshot);
    });
  }

  static async updatedDocument<T>(data: Partial<T>, ...paths: string[]): Promise<void> {
    const documentsRef = ref(firebaseDatabase, paths.join('/'));
    await update(documentsRef, data);
  }

  static async insertDocument<T>(data: T, ...paths: string[]): Promise<void> {
    const documentsRef = ref(firebaseDatabase, paths.join('/'));
    await set(documentsRef, data);
  }

  static async appendDocument<T>(data: T, ...paths: string[]): Promise<void> {
    const documentsRef = ref(firebaseDatabase, paths.join('/'));
    await push(documentsRef, data);
  }

  static async removeDocument(...paths: string[]): Promise<void> {
    const documentsRef = ref(firebaseDatabase, paths.join('/'));
    await remove(documentsRef);
  }

  static async removeDocumentField(...paths: string[]): Promise<void> {
    const documentsRef = ref(firebaseDatabase, paths.join('/'));
    await set(documentsRef, null);
  }

  static async filterDocumentsByField<T>(paths: string[], options: FilterOptions): Promise<T[]> {
    const { filterBy, filterByValue = true } = options;
    const documentsRef = ref(firebaseDatabase, paths.join('/'));
    const filteredQuery = query(documentsRef, orderByChild(filterBy), equalTo(filterByValue));
    const result: T[] = await get(filteredQuery)
      .then(snapshot => {
        if (!snapshot.exists()) return [];
        return snapshot.val();
      })
      .catch(() => []);
    return result;
  }
}

export { FirebaseDatabaseClient };
